// Layer 4 (UI): the selected stage's depth as one tab set (polish.P11, comp
// variant A): What is off · Operating window · Displays · What good looks like
// | Balance · Streams · Rules · Units · Headroom. It replaces the Summary |
// Detail toggle, the four expanders and the Reference-tables tab row. The tab
// list and every count come from `StageDepth.transform.ts`.
import { Fragment, useState } from 'react';
import {
  Pill,
  SurfacePagePanel,
  SurfacePageTabs,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import type { UnitHealth } from '@/versions/v1/schema/unit-operation';
import { ChartExpanderButton, ControlChart, MeterBar, OperatingCurve } from '@/versions/v1/modules/_viz';
import { THROUGHPUT_FRONTIER_PROVENANCE } from '@/versions/v1/shared/operating-curve.derive';
import { MetalAccountingBalanceTab } from '@/versions/v1/modules/metal-accounting/BalanceTab';
import type { PeriodView } from './MetalAccountingLayer.transform';
import type { OffPane as OffPaneModel } from './Circuits.transform';
import { OffPane } from './OffPane';
import { NodeDisplays } from './NodeDisplays';
import { UnitOpGlyph } from './UnitOpGlyph';
import {
  INSIGHT_KIND_LABEL,
  RULE_PRIORITY_TONE,
  RULE_ROLE_LABEL,
  type ConstraintRowView,
  type DiagnosticRuleRow,
  type InsightTableRow,
  type NodeStreamRow,
  type ResolvedUtilizationRank,
  type grindOperatingCurve,
} from './Scene.transform';
import { UNIT_HEALTH_LABEL, curveDisplayYDomain, type StageTabGroups, type StageTabId, type StageUnitRow } from './StageDepth.transform';

const HEALTH_PILL: Record<UnitHealth, 'success' | 'warning' | 'danger'> = {
  ok: 'success',
  warn: 'warning',
  alarm: 'danger',
};

type GrindCurve = ReturnType<typeof grindOperatingCurve>;

const NO_TAB = '' as StageTabId;

export type StageDepthProps = {
  groups: StageTabGroups;
  tab: StageTabId | null;
  onTabChange: (tab: StageTabId) => void;
  stageName: string;
  nodeId: string;
  circuitLabel: string | null;
  pane: OffPaneModel | undefined;
  windowRows: readonly ConstraintRowView[];
  windowHeadline: string;
  grindCurve: GrindCurve | null;
  /** Constraint whose chart a `?reading=rd-c-*` deep link opens. */
  initialOpenChart?: string;
  looksRows: readonly InsightTableRow[];
  looksEntries: number;
  streams: readonly NodeStreamRow[];
  rules: readonly DiagnosticRuleRow[];
  rulesLine: string | null;
  units: readonly StageUnitRow[];
  headroom: readonly ResolvedUtilizationRank[];
  balanceView: PeriodView;
};

function WindowTab({
  rows,
  headline,
  curve,
  initialOpenChart,
}: {
  rows: readonly ConstraintRowView[];
  headline: string;
  curve: GrindCurve | null;
  initialOpenChart?: string;
}) {
  const [openCharts, setOpenCharts] = useState<ReadonlySet<string>>(
    () => new Set(initialOpenChart ? [initialOpenChart] : []),
  );
  const toggle = (id: string) =>
    setOpenCharts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <SurfacePagePanel title="Operating window" meta={headline}>
      <Commentable name="Constraints">
        <div className={curve ? 'grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]' : undefined}>
          <Table wrap={false}>
            <THead>
              <TR>
                <TH>Point</TH>
                <TH>DCS range</TH>
                <TH>Normal / design</TH>
                <TH>Alarms</TH>
                <TH>Window</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <Fragment key={row.id}>
                  <TR id={`plant-model-constraint-${row.id}`}>
                    <TD className="font-medium text-ink-primary">
                      <div className="flex items-center gap-2">
                        {row.chart ? (
                          <ChartExpanderButton
                            expanded={openCharts.has(row.id)}
                            label={`${row.point} control chart`}
                            onToggle={() => toggle(row.id)}
                          />
                        ) : null}
                        {row.point}
                      </div>
                    </TD>
                    <TD className="text-xs text-muted">{row.dcsRange}</TD>
                    <TD className="text-xs text-muted">{row.normalRange}</TD>
                    <TD className="font-mono text-xs text-muted">{row.alarms ?? '—'}</TD>
                    <TD className="min-w-[8rem]">
                      {row.window ? (
                        <MeterBar
                          value={row.window.value ?? 0}
                          max={row.window.max}
                          band={row.window.band ?? undefined}
                          marker={
                            row.window.design !== null
                              ? { value: row.window.design, label: `Design ${row.window.design}` }
                              : row.window.alarm !== null
                                ? { value: row.window.alarm, label: `Alarm ${row.window.alarm}` }
                                : undefined
                          }
                          tone={row.window.tone}
                        />
                      ) : (
                        <span className="text-xs text-muted">—</span>
                      )}
                    </TD>
                  </TR>
                  {row.chart && openCharts.has(row.id) ? (
                    <TR>
                      <TD colSpan={5}>
                        <ControlChart model={row.chart} seriesTone="ink" pointMarkers="highlighted" />
                      </TD>
                    </TR>
                  ) : null}
                </Fragment>
              ))}
            </TBody>
          </Table>
          {curve ? (
            <div data-grind-curve>
              <p className="text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">Throughput vs P80</p>
              <OperatingCurve
                xLabel={curve.xLabel}
                yLabel={curve.yLabel}
                xUnit={curve.xUnit}
                yUnit={curve.yUnit}
                design={curve.design}
                frontier={curve.frontier}
                trail={curve.trail}
                current={curve.current}
                future={curve.future}
                tone={curve.tone}
                height={200}
                caption={curve.caption}
                cursorTooltip={curve.current.label}
                frontierProvenance="[syn]"
                frontierProvenanceTitle={THROUGHPUT_FRONTIER_PROVENANCE}
                throughputMode
                xDomain={curve.xDomain}
                yDomain={curveDisplayYDomain(curve.yDomain)}
              />
            </div>
          ) : null}
        </div>
      </Commentable>
    </SurfacePagePanel>
  );
}

function LooksTab({ rows, entries }: { rows: readonly InsightTableRow[]; entries: number }) {
  return (
    <SurfacePagePanel title="What good looks like" meta={`${rows.length} rows · ${entries} entries`} flush className="overflow-hidden">
      <Table wrap={false}>
        <THead>
          <TR>
            <TH>{INSIGHT_KIND_LABEL.good}</TH>
            <TH>{INSIGHT_KIND_LABEL.bad}</TH>
            <TH>{INSIGHT_KIND_LABEL.insight}</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row, index) => (
            <TR key={index}>
              {(['good', 'bad', 'insight'] as const).map((kind) => (
                <TD
                  key={kind}
                  className={`align-top text-[13px] text-ink-secondary ${
                    kind === 'bad' ? 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)]' : ''
                  }`}
                >
                  {row[kind].text ? (
                    <>
                      <p>{row[kind].text}</p>
                      {row[kind].tags.length > 0 ? (
                        <span className="mt-1 flex flex-wrap gap-1">
                          {row[kind].tags.map((tag) => (
                            <span key={tag} className="font-mono text-xs text-muted">
                              {tag}
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </>
                  ) : null}
                </TD>
              ))}
            </TR>
          ))}
        </TBody>
      </Table>
    </SurfacePagePanel>
  );
}

function StreamsTab({ rows }: { rows: readonly NodeStreamRow[] }) {
  return (
    <SurfacePagePanel title="Streams" flush className="overflow-hidden">
      <Table wrap={false}>
        <THead>
          <TR>
            <TH>In / out</TH>
            <TH>Stream</TH>
            <TH>Flow</TH>
            <TH>Specs</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => (
            <TR key={row.stream.id}>
              <TD className="text-xs uppercase tracking-wide text-muted">
                {row.direction}
                {row.stream.samplePointIds.length > 0 ? (
                  <span className="mt-1 block normal-case tracking-normal">{row.stream.samplePointIds.join(' · ')}</span>
                ) : null}
                {row.stream.isRecycle ? (
                  <span className="mt-1 block normal-case tracking-normal">
                    Recycle
                    {row.loop?.loadRatio ? ` · ${row.loop.loadRatio}` : ''}
                  </span>
                ) : null}
              </TD>
              <TD className="font-medium text-ink-primary">{row.stream.name}</TD>
              <TD className="text-xs text-muted">{row.stream.nominalFlow}</TD>
              <TD>
                {row.specs.length === 0 ? (
                  <span className="text-xs text-muted">—</span>
                ) : (
                  <span className="flex flex-wrap gap-1">
                    {row.specs.map((spec) => (
                      <span
                        key={spec.id}
                        title={spec.provenance}
                        className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-xs text-ink-secondary"
                      >
                        {spec.parameter} {spec.target}
                      </span>
                    ))}
                  </span>
                )}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </SurfacePagePanel>
  );
}

function RulesTab({ rules, rulesLine }: { rules: readonly DiagnosticRuleRow[]; rulesLine: string | null }) {
  return (
    <SurfacePagePanel title="Rules" meta={rulesLine ?? undefined} flush className="overflow-hidden">
      <Table wrap={false}>
        <THead>
          <TR>
            <TH>Pattern</TH>
            <TH>Diagnosis</TH>
            <TH>P</TH>
            <TH>Who</TH>
            <TH>Action</TH>
          </TR>
        </THead>
        <TBody>
          {rules.map((rule) => (
            <TR
              key={rule.id}
              data-rule-row={rule.id}
              className={
                rule.highlighted
                  ? 'bg-[color-mix(in_srgb,var(--state-warning)_12%,transparent)]'
                  : rule.contrast
                    ? 'opacity-70'
                    : undefined
              }
            >
              <TD className="text-[13px] font-semibold text-ink-primary">
                {rule.signalPattern}
                {rule.highlighted ? (
                  <span className="mt-1 block text-xs uppercase tracking-wide text-muted">Playing</span>
                ) : null}
                {rule.contrast ? (
                  <span className="mt-1 block text-xs uppercase tracking-wide text-muted">Contrast</span>
                ) : null}
              </TD>
              <TD className="text-[13px] text-muted">{rule.diagnosis}</TD>
              <TD>
                <Pill tone={RULE_PRIORITY_TONE[rule.priority]}>{rule.priority}</Pill>
              </TD>
              <TD className="whitespace-nowrap text-[13px] text-muted">{RULE_ROLE_LABEL[rule.responsible]}</TD>
              <TD className="text-[13px] text-muted">{rule.recommendedAction}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </SurfacePagePanel>
  );
}

function UnitsTab({ rows }: { rows: readonly StageUnitRow[] }) {
  return (
    <Commentable name="PlantModel">
      <div>
        <SurfacePagePanel title="Units" flush className="overflow-hidden">
          <Table wrap={false}>
            <THead>
              <TR>
                <TH>Unit</TH>
                <TH>Health</TH>
                <TH>Key param</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map(({ unit, health, keyParam }) => (
                <TR key={unit.id}>
                  <TD className="font-medium text-ink-primary">
                    <span className="inline-flex items-center gap-1.5">
                      <UnitOpGlyph type={unit.type} size={14} className="text-muted" aria-hidden />
                      <span>
                        {unit.name}
                        <span className="ml-1.5 font-mono text-xs text-muted">{unit.id}</span>
                      </span>
                    </span>
                  </TD>
                  <TD>
                    <Pill tone={HEALTH_PILL[health]}>{UNIT_HEALTH_LABEL[health]}</Pill>
                  </TD>
                  <TD className="tabular text-xs text-muted">{keyParam}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </SurfacePagePanel>
      </div>
    </Commentable>
  );
}

function HeadroomTab({ rows }: { rows: readonly ResolvedUtilizationRank[] }) {
  return (
    <Commentable name="Utilization">
      <div>
        <SurfacePagePanel title="Headroom" flush className="overflow-hidden">
          <Table wrap={false}>
            <THead>
              <TR>
                <TH>Headroom</TH>
                <TH>Utilization</TH>
                <TH>Basis</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD className="font-medium text-ink-primary">{row.name}</TD>
                  <TD className="tabular">
                    <span className="flex items-center gap-2">
                      <span className="block h-1.5 w-28 overflow-hidden rounded-full bg-[var(--hairline)]">
                        <span className="block h-full rounded-full bg-ink-secondary" style={{ width: `${row.utilizationPct}%` }} />
                      </span>
                      {row.utilizationPct}%
                    </span>
                  </TD>
                  <TD className="text-xs text-muted">{row.basis}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </SurfacePagePanel>
      </div>
    </Commentable>
  );
}

export function StageDepth(props: StageDepthProps) {
  const { groups, tab, onTabChange } = props;
  return (
    <section id="plant-model-stage-depth" className="space-y-3">
      <Commentable name="SubprocessTabs">
        <div className="flex flex-wrap items-center gap-x-1 gap-y-2" data-stage-tabs>
          {groups.reads.length ? (
            <SurfacePageTabs
              aria-label="Stage reads"
              variant="ink"
              // One selection across both groups: the group without it shows none.
              value={tab ?? NO_TAB}
              onChange={onTabChange}
              items={groups.reads}
            />
          ) : null}
          {groups.reads.length && groups.reference.length ? (
            <span aria-hidden className="mx-1.5 h-[18px] w-px bg-[var(--hairline-strong)]" />
          ) : null}
          {groups.reference.length ? (
            <SurfacePageTabs
              aria-label="Stage reference tables"
              variant="ink"
              // One selection across both groups: the group without it shows none.
              value={tab ?? NO_TAB}
              onChange={onTabChange}
              items={groups.reference}
            />
          ) : null}
          {/* Stays on the tab row's line: it gives up width (truncating, the
              full name in its title) before it wraps below the tabs. */}
          <span
            className="ml-auto min-w-[3rem] flex-1 basis-0 truncate text-right text-xs text-muted"
            title={props.stageName}
            data-stage-tabs-label
          >
            {props.stageName}
          </span>
        </div>
      </Commentable>
      <div role="tabpanel" data-stage-tab-body={tab ?? ''}>
        {tab === 'off' && props.pane && props.circuitLabel ? (
          <Commentable name="OffPane">
            <div>
              <OffPane circuitLabel={props.circuitLabel} pane={props.pane} />
            </div>
          </Commentable>
        ) : null}
        {tab === 'window' ? (
          <WindowTab
            key={props.nodeId}
            rows={props.windowRows}
            headline={props.windowHeadline}
            curve={props.grindCurve}
            initialOpenChart={props.initialOpenChart}
          />
        ) : null}
        {tab === 'displays' ? (
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7">
              <NodeDisplays nodeId={props.nodeId} only="tables" stack showPending={false} />
            </div>
            <div className="col-span-12 lg:col-span-5">
              <NodeDisplays nodeId={props.nodeId} only="widgets" stack showPending={false} />
            </div>
          </div>
        ) : null}
        {tab === 'looks' ? <LooksTab rows={props.looksRows} entries={props.looksEntries} /> : null}
        {tab === 'balance' ? <MetalAccountingBalanceTab nodeId={props.nodeId} view={props.balanceView} /> : null}
        {tab === 'streams' ? <StreamsTab rows={props.streams} /> : null}
        {tab === 'rules' ? <RulesTab rules={props.rules} rulesLine={props.rulesLine} /> : null}
        {tab === 'units' ? <UnitsTab rows={props.units} /> : null}
        {tab === 'headroom' ? <HeadroomTab rows={props.headroom} /> : null}
      </div>
    </section>
  );
}
